import './BuyerReviewVendorContractsForm.scss';
import React, {
    forwardRef,
    useEffect,
    useImperativeHandle,
    useRef,
    useState,
} from 'react';
import { Formik } from 'formik';
import * as yup from 'yup';
import { Feedback } from '@availity/form';
import { Input, Label, Spinner } from 'reactstrap';
import { useStateSelector } from '../../../../store/selectors';
import createNotification from '../../../../utils/createNotification';
import { generateGuid } from '../../../../utils/typedHelper';
import {
    defaultTimelineAction,
    TimelineAction,
} from '../../../../slices/marketplace/timeline/models/timeline-actions.model';
import { TimelineActionType } from '../../../../slices/marketplace/timeline/models/timeline-action-type.model';
import { DocumentSection } from '../../../../slices/marketplace/timeline/models/document-section.model';
import { DocumentDetails } from '../../../../slices/marketplace/timeline/models/document-details.model';
import { LinkedDataRequestProject } from '../../../../slices/marketplace/timeline/models/linked-data-request-project.model';
import { BuyerReviewVendorContractsConfig } from '../../../../slices/marketplace/timeline/models/buyer-review-vendor-contracts-config.model';
import { MarketplaceDocumentSectionField } from '../DocumentSectionField/MarketplaceDocumentSectionField';
import { MarketplaceDocumentSectionFieldValidator } from '../DocumentSectionField/MarketplaceDocumentSectionFieldValidator';
import { TimelineParty } from '../../../../slices/marketplace/timeline/models/enums/timeline-party.enum';
import { DataTable } from '../../../../models/DataRequestHub/DataTable';
import { VDRFolderView } from '../../../../slices/marketplace/data-room/models/vdr-folder-view.model';

interface BuyerReviewVendorContractsFormProps {
    onSubmit(
        action: TimelineAction,
        config: BuyerReviewVendorContractsConfig
    ): void;
    disabled: boolean;
    documentsSections: DocumentSection[];
    setDocumentSectionList: (sections: DocumentSection[]) => void;
    documentDetailsList: DocumentDetails[];
    dataRequestProjects: LinkedDataRequestProject[];
    engagementId?: number;
    isTemplate: boolean;
    linkedAction?: TimelineAction;
    buyerReviewVendorContractsConfigState: BuyerReviewVendorContractsConfig | null;
}

interface VdrFolderOption {
    name: string;
    fullPath: string;
}

const flattenFolders = (folder: VDRFolderView): VdrFolderOption[] => {
    const result: VdrFolderOption[] = [
        { name: folder.name, fullPath: folder.fullPath },
    ];
    if (folder.subFolders) {
        folder.subFolders.forEach((sub) => {
            result.push(...flattenFolders(sub));
        });
    }
    return result;
};

export const BuyerReviewVendorContractsForm = forwardRef(
    (props: BuyerReviewVendorContractsFormProps, ref) => {
        const axios = useStateSelector((state) => state.core.axios);
        const formRef = useRef<any>();

        useImperativeHandle(ref, () => ({
            handleSubmit() {
                formRef?.current?.handleSubmit();
            },
        }));

        const config = props.buyerReviewVendorContractsConfigState;

        const initialProjectId = config?.dataRequestProjectId ?? '0';
        const initialDataTableId = config?.dataTableId ?? 0;
        const vendorContractsSection = props.documentsSections.find(
            (s) => s.name === 'Vendor Contracts'
        );
        const initialDocumentSection =
            config?.documentSectionId ?? vendorContractsSection?.id ?? '-1';
        const initialVdrFolder = config?.vdrFolderPath ?? '';

        const [dataTables, setDataTables] = useState<DataTable[]>([]);
        const [isLoadingDataTables, setIsLoadingDataTables] = useState(false);
        const [vdrFolders, setVdrFolders] = useState<VdrFolderOption[]>([]);
        const [isLoadingVdr, setIsLoadingVdr] = useState(false);

        const fetchDataTablesForProject = (
            projectId: string,
            tableIdToResolve?: number
        ) => {
            const project = props.dataRequestProjects.find(
                (p) => p.id === projectId
            );
            if (!project) {
                setDataTables([]);
                return;
            }

            setIsLoadingDataTables(true);

            if (props.isTemplate) {
                axios
                    .get('/api/dataRequestTemplateProject/dataTables')
                    .then((response: any) => {
                        if (response.status === 200) {
                            const allTables: DataTable[] = response.data;
                            const filtered = allTables.filter((t) =>
                                project.dataTableTemplateIds?.includes(t.id)
                            );
                            setDataTables(filtered);
                        } else {
                            createNotification(
                                'An error occurred while fetching data tables.',
                                'error'
                            );
                        }
                    })
                    .finally(() => setIsLoadingDataTables(false));
            } else {
                if (!project.dataRequestProjectId) {
                    setDataTables([]);
                    setIsLoadingDataTables(false);
                    return;
                }
                axios
                    .get(
                        `/api/DataRequestProjects/${project.dataRequestProjectId}`
                    )
                    .then((response: any) => {
                        if (response.status === 200) {
                            const tables: DataTable[] =
                                response.data.dataTables ?? [];
                            setDataTables(tables);
                            if (tableIdToResolve && tableIdToResolve > 0) {
                                const exactMatch = tables.find(
                                    (t) => t.id === tableIdToResolve
                                );
                                if (!exactMatch) {
                                    const templateMatch = tables.find(
                                        (t) =>
                                            t.originalTableId ===
                                            tableIdToResolve
                                    );
                                    if (templateMatch) {
                                        formRef.current?.setFieldValue(
                                            'dataTable',
                                            templateMatch.id
                                        );
                                    }
                                }
                            }
                        } else {
                            createNotification(
                                'An error occurred while fetching data tables.',
                                'error'
                            );
                        }
                    })
                    .finally(() => setIsLoadingDataTables(false));
            }
        };

        useEffect(() => {
            if (!props.isTemplate && props.engagementId) {
                setIsLoadingVdr(true);
                axios
                    .get(
                        `api/VirtualDataRooms/${props.engagementId}?sourceType=Engagement`
                    )
                    .then((response: any) => {
                        if (response.status === 200) {
                            const rootFolder: VDRFolderView = response.data;
                            setVdrFolders(flattenFolders(rootFolder));
                        } else {
                            createNotification(
                                'An error occurred while fetching VDR folders.',
                                'error'
                            );
                        }
                    })
                    .finally(() => setIsLoadingVdr(false));
            }

            if (initialProjectId && initialProjectId !== '0') {
                fetchDataTablesForProject(
                    initialProjectId,
                    initialDataTableId > 0 ? initialDataTableId : undefined
                );
            }
        }, []);

        const validationSchema = yup.object().shape({
            dataRequestProject: yup
                .string()
                .required('Data Request Project is required')
                .notOneOf(['0'], 'Data Request Project is required'),
            dataTable: yup
                .number()
                .required('Data Table is required')
                .min(1, 'Data Table is required'),
            documentSection:
                MarketplaceDocumentSectionFieldValidator.documentSectionValidationRules(
                    props.documentsSections,
                    TimelineParty.Buyer
                ),
            vdrFolder: props.isTemplate
                ? yup.string().nullable()
                : yup
                      .string()
                      .required('VDR Folder is required')
                      .notOneOf([''], 'VDR Folder is required'),
        });

        return (
            <div className="buyer-review-vendor-contracts-form">
                <Formik
                    innerRef={formRef}
                    initialValues={{
                        dataRequestProject: initialProjectId,
                        dataTable: initialDataTableId,
                        documentSection: initialDocumentSection,
                        vdrFolder: initialVdrFolder,
                    }}
                    validationSchema={validationSchema}
                    onSubmit={(values) => {
                        const configId = config?.id ?? generateGuid();
                        props.onSubmit(
                            {
                                ...defaultTimelineAction,
                                actionType:
                                    TimelineActionType.BuyerReviewVendorContracts,
                                buyerReviewVendorContractsConfigId: configId,
                            },
                            {
                                id: configId,
                                dataRequestProjectId:
                                    values.dataRequestProject !== '0'
                                        ? values.dataRequestProject
                                        : null,
                                dataTableId:
                                    values.dataTable > 0
                                        ? values.dataTable
                                        : null,
                                documentSectionId:
                                    values.documentSection !== '-1'
                                        ? values.documentSection
                                        : null,
                                vdrFolderPath: values.vdrFolder || null,
                            }
                        );
                    }}>
                    {({ values, setFieldValue }) => (
                        <div className="buyer-review-vendor-contracts">
                            <Label for="dataRequestProject">
                                Data Request Project
                            </Label>
                            <Input
                                type="select"
                                name="dataRequestProject"
                                id="dataRequestProject"
                                disabled={props.disabled}
                                value={values.dataRequestProject}
                                onChange={(e) => {
                                    const projectId = e.target.value;
                                    setFieldValue(
                                        'dataRequestProject',
                                        projectId
                                    );
                                    setFieldValue('dataTable', 0);
                                    setDataTables([]);
                                    if (projectId !== '0') {
                                        fetchDataTablesForProject(projectId);
                                    }
                                }}>
                                <option value="0">Select</option>
                                {props.dataRequestProjects.map((project) => (
                                    <option key={project.id} value={project.id}>
                                        {project.projectName}
                                    </option>
                                ))}
                            </Input>
                            <Feedback name="dataRequestProject" />

                            <Label for="dataTable">Data Table</Label>
                            {isLoadingDataTables ? (
                                <Spinner size="sm" />
                            ) : (
                                <Input
                                    type="select"
                                    name="dataTable"
                                    id="dataTable"
                                    disabled={
                                        props.disabled ||
                                        dataTables.length === 0
                                    }
                                    value={values.dataTable}
                                    onChange={(e) =>
                                        setFieldValue(
                                            'dataTable',
                                            Number(e.target.value)
                                        )
                                    }>
                                    <option value={0}>Select</option>
                                    {dataTables.map((table) => (
                                        <option key={table.id} value={table.id}>
                                            {table.name}
                                        </option>
                                    ))}
                                </Input>
                            )}
                            <Feedback name="dataTable" />

                            <MarketplaceDocumentSectionField
                                documentsSections={props.documentsSections}
                                disabled={props.disabled}
                                setDocumentSectionList={
                                    props.setDocumentSectionList
                                }
                                documentDetailsList={props.documentDetailsList}
                                type="buyers"
                                sectionsModalMode="built-in"
                            />

                            <Label for="vdrFolder">VDR Folder</Label>
                            {isLoadingVdr ? (
                                <Spinner size="sm" />
                            ) : (
                                <Input
                                    type="select"
                                    name="vdrFolder"
                                    id="vdrFolder"
                                    disabled={
                                        props.disabled || props.isTemplate
                                    }
                                    value={values.vdrFolder}
                                    onChange={(e) =>
                                        setFieldValue(
                                            'vdrFolder',
                                            e.target.value
                                        )
                                    }>
                                    <option value="">Select</option>
                                    {vdrFolders.map((folder) => (
                                        <option
                                            key={folder.fullPath}
                                            value={folder.fullPath}>
                                            {folder.name}
                                        </option>
                                    ))}
                                </Input>
                            )}
                            <Feedback name="vdrFolder" />
                        </div>
                    )}
                </Formik>
            </div>
        );
    }
);
